import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/shared/libs/axiosClient";
import { queryKeys } from "@/shared/libs/queryClient";
import { toast } from "@/shared/hooks/useToast";
import type {
  AnomalyDetectionRule,
  AnomalyDetectionCheckType,
  CreateAnomalyDetectionRuleDto,
  UpdateAnomalyDetectionRuleDto,
} from "@/types/preferences_types";

// ============================================
// API 함수들
// ============================================

/**
 * 모든 이상 탐지 규칙 조회
 */
async function fetchAnomalyDetectionRules(): Promise<AnomalyDetectionRule[]> {
  return api.get<AnomalyDetectionRule[]>("/anomaly-detection-rules/");
}

/**
 * 특정 규칙 조회
 */
async function fetchAnomalyDetectionRuleById(ruleId: number): Promise<AnomalyDetectionRule> {
  return api.get<AnomalyDetectionRule>(`/anomaly-detection-rules/${ruleId}`);
}

/**
 * 사용 가능한 체크 타입 목록 조회
 */
async function fetchCheckTypes(): Promise<readonly AnomalyDetectionCheckType[]> {
  const response = await api.get<{ checkTypes: string[] }>("/anomaly-detection-rules/check-types");
  // 백엔드가 정확히 AnomalyDetectionCheckType 값들을 반환하므로 타입 단언
  return response.checkTypes as AnomalyDetectionCheckType[];
}

/**
 * 새로운 규칙 생성
 */
async function createAnomalyDetectionRule(
  data: CreateAnomalyDetectionRuleDto
): Promise<AnomalyDetectionRule> {
  return api.post<AnomalyDetectionRule>("/anomaly-detection-rules/", data);
}

/**
 * 기존 규칙 수정
 */
async function updateAnomalyDetectionRule(
  ruleId: number,
  data: UpdateAnomalyDetectionRuleDto
): Promise<AnomalyDetectionRule> {
  return api.patch<AnomalyDetectionRule>(`/anomaly-detection-rules/${ruleId}`, data);
}

/**
 * 규칙 삭제 (여러 개)
 */
async function deleteAnomalyDetectionRules(ruleIds: number[]): Promise<void> {
  // DELETE 요청에 body로 rule_ids를 전송
  return api.delete<void>("/anomaly-detection-rules/", {
    data: ruleIds,
  });
}

// ============================================
// Query 훅들
// ============================================

/**
 * 모든 이상 탐지 규칙 조회 Query
 */
export function useAnomalyDetectionRulesQuery() {
  return useQuery({
    queryKey: queryKeys.anomalyDetectionRules.list(),
    queryFn: fetchAnomalyDetectionRules,
  });
}

/**
 * 특정 규칙 조회 Query
 */
export function useAnomalyDetectionRuleDetailQuery(ruleId: number) {
  return useQuery({
    queryKey: queryKeys.anomalyDetectionRules.detail(ruleId),
    queryFn: () => fetchAnomalyDetectionRuleById(ruleId),
    enabled: !!ruleId,
  });
}

/**
 * 체크 타입 목록 조회 Query
 */
export function useCheckTypesQuery() {
  return useQuery<readonly AnomalyDetectionCheckType[]>({
    queryKey: queryKeys.anomalyDetectionRules.checkTypes(),
    queryFn: fetchCheckTypes,
    staleTime: Infinity, // 체크 타입 목록은 변경이 거의 없으므로 무한 캐시
  });
}

// ============================================
// Mutation 훅들
// ============================================

/**
 * 규칙 생성 Mutation
 */
export function useCreateAnomalyDetectionRuleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAnomalyDetectionRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.anomalyDetectionRules.lists() });
      toast({
        title: "규칙 생성 완료",
        description: "이상 탐지 규칙이 성공적으로 생성되었습니다.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "규칙 생성 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * 규칙 수정 Mutation
 */
export function useUpdateAnomalyDetectionRuleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ruleId, data }: { ruleId: number; data: UpdateAnomalyDetectionRuleDto }) =>
      updateAnomalyDetectionRule(ruleId, data),
    onSuccess: (_, { ruleId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.anomalyDetectionRules.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.anomalyDetectionRules.detail(ruleId) });
      toast({
        title: "규칙 수정 완료",
        description: "이상 탐지 규칙이 성공적으로 수정되었습니다.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "규칙 수정 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * 규칙 삭제 Mutation
 */
export function useDeleteAnomalyDetectionRulesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAnomalyDetectionRules,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.anomalyDetectionRules.lists() });
      toast({
        title: "규칙 삭제 완료",
        description: "이상 탐지 규칙이 성공적으로 삭제되었습니다.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "규칙 삭제 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
